// Angular
import { Component, ChangeDetectionStrategy, inject, signal, DestroyRef, DOCUMENT, } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';

// Store
import { AppState } from '@app/core/auth/auth.store';
import { Store } from '@ngrx/store';
import { AuthService, Login } from '@app/core/auth';
// RxJs & Lodash
import { asyncScheduler } from 'rxjs';
import { tap, catchError } from 'rxjs/operators'; 
import { get } from 'lodash';
// Model
import { IUserLogin } from '@app/core/auth/user.model';
// Service
import { GlobalErrorHandler } from '@core/utils';
// Material
import { MatDialogRef, MAT_DIALOG_DATA, MatDialogTitle, MatDialogContent, MatDialogActions, MatDialogClose } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { CdkScrollable } from '@angular/cdk/scrolling';
import { MatButton } from '@angular/material/button';
// Transloco
import { TranslocoService, TranslocoDirective } from '@jsverse/transloco';

@Component({
    selector: 'app-switch-company-dialog',
    templateUrl: './switch-company-dialog.component.html',
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [MatDialogTitle, TranslocoDirective, FormsModule, CdkScrollable, MatDialogContent, MatDialogActions, MatButton, MatDialogClose]
})
export class SwitchCompanyDialogComponent {

  // Private
  private readonly dialogRef = inject<MatDialogRef<SwitchCompanyDialogComponent>>(MatDialogRef);
  private readonly router = inject(Router);
  private readonly auth = inject(AuthService);
  private readonly globalErrorHandler = inject(GlobalErrorHandler);
  private readonly store = inject<Store<AppState>>(Store);
  private readonly snackBar = inject(MatSnackBar);
  private readonly translocoService = inject(TranslocoService);
  private document = inject<Document>(DOCUMENT);
  private readonly destroyRef = inject(DestroyRef);
  readonly data = inject(MAT_DIALOG_DATA);


  // Public 
  isDefault = false; 
  loading = signal<boolean>(false);

  /**
   * Switch company access to requested company
   * @param e: Event
   * @returns void
   */
  onConfirm(e: Event): void {
    this.loading.set(true);
    const switchData: IUserLogin = {
      username: null,
      password: null,
      company_id: get(this.data, 'switchTo.id'),
      company_default: this.isDefault,
    };
    this.auth.login(switchData).pipe(
      tap((response: any) => this._handleSwitchResponse(response)),
      catchError(error => this.globalErrorHandler.handleError('on-confirm-switch-company-access', error)),
      takeUntilDestroyed(this.destroyRef),
    ).subscribe();
  }

  /**
   * Handle switch company access to requested company response
   * @param response: any
   * @returns void
   */
  private _handleSwitchResponse(response: any): void {
    const label_close = this.translocoService.translate('label_close');
    if (response && response.success) {
      this.snackBar.open(this.translocoService.translate('dynamic_accessing_company_now_message', { name: this.data?.switchTo?.name }), label_close, {
      panelClass: ['success-dialog'] 
    });
      this.store.dispatch(
        new Login({
          user: get(response, 'data'),
          authToken: get(response, 'data.token.access'),
        })
      );
      this.router.navigate([`/${this.translocoService.getActiveLang()}`]);
      asyncScheduler.schedule(() => {
        this.loading.set(false);
        this.dialogRef.close();
        this.document.location.reload();
      }, 2000);
    } else {
      this.loading.set(false);
      this.snackBar.open(this.translocoService.translate('sentence_something_went_wrong'), label_close, {
        panelClass: ['danger-dialog']
      });
    }
  }
}
